import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import {
  Gem, Search, Compass, Map, Users, GitCompare, LineChart, TrendingUp,
  Coins, Factory, Store, ShieldAlert, Bell, FileText, ClipboardList,
  Archive, Settings, ArrowRight, Sparkles, CheckCircle2, XCircle,
  AlertCircle, PowerOff,
} from "lucide-react";
import { TopBar } from "@/components/PageShell";
import ImageSlot from "@/components/ImageSlot";

// Paleta institucional Hermes Mineral
export const MINERAL_COLORS = {
  navy:       "#0B1B2F",
  navy2:      "#10253E",
  navy3:      "#132D4A",
  gold:       "#C3A05A",
  goldDark:   "#A88745",
  goldLight:  "#D9BF82",
  offWhite:   "#F6F2E9",
  bg:         "#FAF8F3",
  divider:    "#E4E7EC",
  muted:      "#667085",
};

const MENU = [
  { to: "",              label: "Visão Geral",     icon: Gem,          test: "mineral-nav-home" },
  { to: "pesquisar",     label: "Pesquisar",       icon: Search,       test: "mineral-nav-search" },
  { to: "descobrir",     label: "Descobrir Região", icon: Compass,     test: "mineral-nav-discover" },
  { to: "mapa",          label: "Mapa Minerário",  icon: Map,          test: "mineral-nav-map" },
  { to: "titulares",     label: "Titulares",       icon: Users,        test: "mineral-nav-holders" },
  { to: "comparar",      label: "Comparar",        icon: GitCompare,   test: "mineral-nav-compare" },
  { to: "classificacoes",label: "Classificações",  icon: LineChart,    test: "mineral-nav-rankings" },
  { to: "tendencias",    label: "Tendências",      icon: TrendingUp,   test: "mineral-nav-trends" },
  { to: "economica",     label: "Inteligência Econômica", icon: Coins, test: "mineral-nav-econ" },
  { to: "producao",      label: "Produção Mineral",icon: Factory,      test: "mineral-nav-prod" },
  { to: "ofertas",       label: "Áreas em Oferta", icon: Store,        test: "mineral-nav-offers" },
  { to: "barragens",     label: "Barragens",       icon: ShieldAlert,  test: "mineral-nav-dams" },
  { to: "monitoramentos",label: "Monitoramentos",  icon: Bell,         test: "mineral-nav-monitor" },
  { to: "alertas",       label: "Alertas",         icon: AlertCircle,  test: "mineral-nav-alerts" },
  { to: "relatorios",    label: "Relatórios",      icon: FileText,     test: "mineral-nav-reports" },
  { to: "meus-pedidos",  label: "Meus Pedidos",    icon: ClipboardList,test: "mineral-nav-orders" },
  { to: "meus-relatorios", label: "Meus Relatórios", icon: Archive,    test: "mineral-nav-mine" },
  { to: "admin/produtos", label: "Admin · Produtos", icon: Settings,   test: "mineral-nav-admin-products" },
];

export function MineralLayout() {
  const location = useLocation();
  return (
    <div className="flex-1 min-w-0 flex flex-col" style={{ height: "100vh" }} data-testid="mineral-layout">
      <TopBar/>
      {/* Header institucional */}
      <div className="relative overflow-hidden"
           style={{ background: `linear-gradient(120deg, ${MINERAL_COLORS.navy} 0%, ${MINERAL_COLORS.navy2} 60%, ${MINERAL_COLORS.navy3} 100%)` }}>
        <div className="absolute inset-0 pointer-events-none opacity-30"
             style={{ background: `radial-gradient(60% 100% at 100% 0%, ${MINERAL_COLORS.gold}44 0%, transparent 70%)` }}/>
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 py-6 flex items-center justify-between gap-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center"
                 style={{ background: `${MINERAL_COLORS.gold}22`, border: `1px solid ${MINERAL_COLORS.gold}77` }}>
              <Gem className="w-6 h-6" style={{ color: MINERAL_COLORS.gold }}/>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em]" style={{ color: MINERAL_COLORS.goldLight }}>
                Inteligência
              </div>
              <h1 className="font-serif text-3xl md:text-4xl" style={{ color: "#fff", letterSpacing: "0.02em" }}>
                HERMES{" "}<span style={{ color: MINERAL_COLORS.gold }}>MINERAL</span>
              </h1>
            </div>
          </div>
          <Link to="/dashboard"
            className="hidden md:inline-flex items-center gap-2 text-xs px-3 py-1.5 rounded-full"
            style={{ background: `${MINERAL_COLORS.gold}22`, color: MINERAL_COLORS.goldLight,
                       border: `1px solid ${MINERAL_COLORS.gold}55` }}
            data-testid="mineral-back-hermes">
            ← Voltar ao Hermes
          </Link>
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 pb-5 text-sm" style={{ color: MINERAL_COLORS.goldLight }}>
          Inteligência minerária baseada em dados oficiais.
        </div>
      </div>

      {/* Body — layout de 2 colunas com scroll interno.
          O aside NUNCA se move: fica sempre na mesma posição.
          O <main> tem o próprio overflow-y-auto, então só o conteúdo rola. */}
      <div className="flex flex-1 min-h-0">
        <aside
          className="w-64 shrink-0 bg-white/95 backdrop-blur border-r border-slate-200 overflow-y-auto"
          data-testid="mineral-sidebar"
        >
          <div className="px-3 pt-4 pb-2 text-[10px] uppercase tracking-[0.24em] text-slate-500 font-semibold">
            Módulos · Hermes Mineral
          </div>
          <nav className="p-3 pt-1 space-y-0.5">
            {MENU.map(({ to, label, icon: I, test }) => {
              const target = to ? `/mineral/${to}` : "/mineral";
              return (
                <NavLink key={to || "home"} to={target} end={to === ""}
                  data-testid={test}
                  className={({ isActive }) =>
                    `flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition ` +
                    (isActive
                      ? "bg-[#0B1B2F] text-[#D9BF82] font-medium"
                      : "text-slate-700 hover:bg-slate-50")
                  }>
                  <I className="w-4 h-4"/> {label}
                </NavLink>
              );
            })}
          </nav>
        </aside>

        {/* Coluna direita: rola internamente, sem afetar a posição do menu */}
        <main className="flex-1 min-w-0 overflow-y-auto">
          <div className="w-full px-6 lg:px-8 py-6">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}

// ---------------- Visão Geral ----------------
export function MineralHome() {
  const [status, setStatus] = useState(null);
  useEffect(() => { api.get("/mineral/status").then(r => setStatus(r.data)).catch(()=>{}); }, []);

  return (
    <div className="space-y-6" data-testid="mineral-home">
      {/* Hero — imagem 4K institucional (slot: mineral_hero) */}
      <section className="rounded-2xl relative overflow-hidden h-[360px]">
        <ImageSlot slot="mineral_hero" alt="Hermes Mineral · inteligência territorial" className="absolute inset-0 w-full h-full object-cover"/>
        {/* Camada escura para contraste do texto (esquerda dominante) */}
        <div className="absolute inset-0" style={{ background: `linear-gradient(90deg, rgba(11,27,47,0.82) 0%, rgba(11,27,47,0.55) 45%, rgba(11,27,47,0.15) 78%, rgba(11,27,47,0) 100%)` }}/>
        <div className="absolute -right-16 -top-16 w-72 h-72 rounded-full opacity-30 blur-3xl"
             style={{ background: MINERAL_COLORS.gold }}/>
        <div className="relative h-full flex flex-col justify-center p-8 md:p-10 max-w-2xl">
          <div className="text-[11px] uppercase tracking-[0.28em] font-semibold mb-3" style={{ color: MINERAL_COLORS.goldLight }}>
            HERMES · MINERAL
          </div>
          <h2 className="font-serif text-3xl md:text-5xl leading-[1.05] text-white mb-3" style={{ textShadow: "0 2px 20px rgba(0,0,0,0.35)" }}>
            Inteligência territorial para <span style={{ color: MINERAL_COLORS.gold }} className="italic">decisões mais seguras</span>.
          </h2>
          <p className="text-sm md:text-base text-white/90 max-w-xl" style={{ textShadow: "0 1px 10px rgba(0,0,0,0.45)" }}>
            Base ANM oficial · SIGMINE · CFEM · TAH · AMB · IBGE — sem invenção, sem alucinação.
          </p>
        </div>
      </section>

      {/* KPIs */}
      <section className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <Kpi label="Base ANM" value={status ? `${status.providers_healthy}/${status.providers_total}` : "—"} sub="providers ok"/>
        <Kpi label="Substâncias" value="—" sub="aguardando ingestão"/>
        <Kpi label="Titulares" value="—" sub="aguardando ingestão"/>
        <Kpi label="Estados" value={status?.flags?.mineral_module_enabled ? "27" : "—"} sub="IBGE oficial"/>
        <Kpi label="Municípios" value={status?.flags?.mineral_module_enabled ? "5.570" : "—"} sub="IBGE oficial"/>
        <Kpi label="Última Atualização" value={status ? new Date(status.checked_at).toLocaleDateString("pt-BR") : "—"} sub=""/>
      </section>

      {/* Atalhos */}
      <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Shortcut to="/mineral/pesquisar" icon={Search} title="Pesquisar" desc="Por substância, região ou titular"/>
        <Shortcut to="/mineral/descobrir" icon={Compass} title="Descobrir Região" desc="Encontre potencial minerário"/>
        <Shortcut to="/mineral/mapa" icon={Map} title="Mapa Geoespacial" desc="Poligonais oficiais SIGMINE"/>
        <Shortcut to="/mineral/economica" icon={Sparkles} title="Inteligência Econômica" desc="CFEM, TAH, produção AMB"/>
      </section>

      {/* Providers status */}
      {status?.providers && (
        <section className="bg-white rounded-2xl border border-slate-200 p-5" data-testid="mineral-providers-panel">
          <div className="text-[11px] uppercase tracking-widest text-slate-500 mb-3">Fontes oficiais</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {status.providers.map(p => (
              <ProviderRow key={p.code} p={p}/>
            ))}
          </div>
          <p className="text-xs text-slate-500 mt-4">
            Regra Hermes: providers só ficam <b>homologados</b> após validação técnica real da fonte.
            Nada de dado inventado.
          </p>
        </section>
      )}
    </div>
  );
}

function Kpi({ label, value, sub }) {
  return (
    <div className="bg-white border rounded-xl p-3" style={{ borderColor: MINERAL_COLORS.divider }}>
      <div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
      <div className="text-2xl font-heading font-semibold" style={{ color: MINERAL_COLORS.navy }}>{value}</div>
      {sub && <div className="text-[11px] text-slate-500">{sub}</div>}
    </div>
  );
}

function Shortcut({ to, icon: I, title, desc }) {
  return (
    <Link to={to} className="group bg-white border rounded-2xl p-5 hover:border-[#C3A05A] transition"
          style={{ borderColor: MINERAL_COLORS.divider }}>
      <div className="w-10 h-10 rounded-lg flex items-center justify-center mb-3"
           style={{ background: `${MINERAL_COLORS.gold}22`, color: MINERAL_COLORS.goldDark }}>
        <I className="w-5 h-5"/>
      </div>
      <div className="font-semibold" style={{ color: MINERAL_COLORS.navy }}>{title}</div>
      <div className="text-xs text-slate-500 mt-1">{desc}</div>
      <div className="mt-3 text-xs inline-flex items-center gap-1 group-hover:gap-2 transition"
           style={{ color: MINERAL_COLORS.goldDark }}>
        Abrir <ArrowRight className="w-3 h-3"/>
      </div>
    </Link>
  );
}

function ProviderRow({ p }) {
  const map = {
    healthy:  { Icon: CheckCircle2, tone: "text-emerald-700 bg-emerald-50 border-emerald-200", label: "Funcionando" },
    degraded: { Icon: AlertCircle,  tone: "text-amber-700 bg-amber-50 border-amber-200",       label: "Parcial" },
    failed:   { Icon: XCircle,      tone: "text-rose-700 bg-rose-50 border-rose-200",           label: "Com Problema" },
    disabled: { Icon: PowerOff,     tone: "text-slate-500 bg-slate-50 border-slate-200",        label: "Desativado" },
  };
  const cfg = map[p.status] || map.disabled;
  const { Icon } = cfg;
  return (
    <div className={`flex items-center gap-2 border rounded-lg px-3 py-2 ${cfg.tone}`} data-testid={`mineral-provider-${p.code}`}>
      <Icon className="w-4 h-4 shrink-0"/>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium truncate">{p.name}</div>
        <div className="text-[11px] truncate opacity-80">{p.detail || p.reason || cfg.label}</div>
      </div>
    </div>
  );
}

// ---------------- Placeholder pages (Fases 1-5) ----------------
export function MineralPlaceholder({ title, fase, description }) {
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-10 text-center" data-testid="mineral-placeholder">
      <div className="w-16 h-16 rounded-2xl mx-auto flex items-center justify-center mb-4"
           style={{ background: `${MINERAL_COLORS.gold}22`, color: MINERAL_COLORS.goldDark }}>
        <Gem className="w-8 h-8"/>
      </div>
      <h2 className="font-heading text-2xl font-semibold mb-2" style={{ color: MINERAL_COLORS.navy }}>
        {title}
      </h2>
      <div className="text-xs uppercase tracking-widest mb-3" style={{ color: MINERAL_COLORS.goldDark }}>
        {fase}
      </div>
      <p className="text-sm text-slate-600 max-w-lg mx-auto">{description}</p>
      <p className="text-[11px] text-slate-400 mt-6">
        Regra Hermes: só entra no ar após homologação real da fonte oficial.
      </p>
    </div>
  );
}
